import { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { deleteData, fetchData, postData } from "../../../services";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/index";
import UploadFile from "../../Utils/UploadFile";
import { useSnackbar } from "../../../context/popup";
import { useLoading } from "../../../components/Loading";
import GeneralDiscountFilterDialog from "./components/GeneralDiscountFilterDialog.js";

const GeneralDiscountList = () => {
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector((state) => state?.generalDiscountStore?.currentPage);
  const rowsPerPage = useSelector((state) => state?.generalDiscountStore?.rowsPerPage);
  const totalPages = useSelector((state) => state?.generalDiscountStore?.totalPages);

  const [search, setSearch] = useState("");
  const [isRefresh, setIsRefresh] = useState(false);
  const [discountData, setDiscountData] = useState([]);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    productId: "",
    policyId: "",
    isActive: "",
  });

  useEffect(() => {
    fetchDiscountData();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //table get Data
  const fetchDiscountData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/general/discount?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}`;
      //product
      if(filterData.productId !== "") {
        url += `&productId=${filterData.productId}`;
      }
      //for active and inactive status
      if(filterData.isActive !== "") {
          url += `&isActive=${filterData.isActive}`;
      }
      //policy type
      if(filterData.policyId !== "") {
          url += `&policyTypeId=${filterData.policyId}`;
      }
      const response = await fetchData(url);
      const responseData = await response?.data?.items;
      if(response?.success){
        setDiscountData(responseData);
        dispatch(setTotalPages(response?.data?.totalPages)); 
      }
      else{
        showSnackbar("Error Fetching Discount Data","error");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    }
    finally {
      setCircleLoading(false);
    }
  };

   //file upload api function
  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true)
    setUploadSuccess(false)

    try {
        const formData = new FormData()

        formData.append('file', file)

        const result = await postData(`/reference/master/bulkUpload/general-discount`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });        

        if (result?.success) {
            setUploadSuccess(true)
            showSnackbar(result?.message,"success");
            setOpenUploadFileDialog(false);
            setClearFileOnSuccess(true);
            fetchDiscountData(); 
        }
        else {
            setUploadSuccess(false);
            showSnackbar('Faiiled to upload file', "error")
        }

    } catch (error) {
          const errorMessage = error?.response?.data?.message;              
          console.error('Upload error:', error || errorMessage)
          showSnackbar(errorMessage, "error")
    } 
    finally {
          setFileUploading(false)
    }
  };

    //delete api function
  const handleDelete = async (row) => {
      try {
          const response = await deleteData(`/reference/general/discount-delete`, {ids:[row?.id]});
          setIsRefresh(!isRefresh);
          if(response?.success){
            showSnackbar(response?.message, "success");
          }
          else{
            showSnackbar(response?.message, "error");
          }
      } 
      catch (error) {
        console.error("Error deleting data:", error);
        showSnackbar(error?.response?.data?.message, "error");
      }
  };

  //table columns
  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "Discount Name",
      renderCell: (row) => row.name,
    },
    {
      key: "maxLimit",
      label: "Max Limit",
      renderCell: (row) => row.maxLimit,
    },
    {
      key: "description",
      label: "Description",
      renderCell: (row) => row.description,
    },
    {
      key: "mandatory",
      label: "Mandatory",
      renderCell: (row) => (
        <span>
          {row?.mandatory ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Yes</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>No</span>
          )}
        </span>
      ),
    },
    {
      key: "product",
      label: "Product",
      renderCell: (row) => row.product?.keyName,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => row.createdAt,
    },
    {
      key: "active",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      )
    },
  ];

  //table rows
  const rows = discountData.map((discount, index) => ({
    ...discount,
    sno: index + 1,
  }));

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleEdit = (row) => {
    navigate(`/general-master/general-discount/edit-discount/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/general-master/general-discount/view-discount/${row?.id}`);
  };

  const handleNewDiscount = () => {
    navigate("/general-master/general-discount/add-discount");
  };

  const handleFileUpload = () => {
    setOpenUploadFileDialog(true);
  };

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                General Discount management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of Discounts that you can get details of the discount.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
              variant="gradient"
              color="dark"
              size="small"
              startIcon={<FilterAlt/>}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => handleFileUpload()}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleNewDiscount()}
              >
                Add Discount
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
              totalPages,
              currentPage,
              onPageChange: (page) => handlePaginationChange(page),
              rowsPerPage,
              onRowsPerPageChange: (rowsPerPage) => handleRowsPerPageChange(rowsPerPage),
              rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel="No General Discounts found"
          handleSearch={handleSearch}
        />
      </Card>

    <UploadFile
      open={openUploadFileDialog}
      setOpen={setOpenUploadFileDialog}
      onUploadSuccess={handleFileUploadSuccess}
      fileTypes={['csv', 'xlsx']}
      fileUploading={fileUploading}
      uploadSuccess={uploadSuccess}
      setUploadSuccess={setUploadSuccess}
      setFileUploading={setFileUploading}
      clearFileOnSuccess={clearFileOnSuccess}
    />
     <GeneralDiscountFilterDialog
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      filterData={filterData}
      setFilterData={setFilterData}
      onApply={() => {
          setAnchorEl(null);   // close popup
          fetchDiscountData();  // call API after filter applied
      }}
      />
    </DashboardLayout>
  );
};

export default GeneralDiscountList;
